import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './EventsDashboardNew.css'
import { Link } from 'react-router-dom'
import { IoIosAdd } from 'react-icons/io'
import Axios from 'axios'
import { IoStar } from 'react-icons/io5'
import ClipLoader from 'react-spinners/ClipLoader'
import { API_URL } from '../../config'

const url = `${API_URL}/apps/kscanner/webservices.php`

// Strips everything except digits, dot and minus, so "14,800.00", "14 800.00"
// and "R14800" all parse correctly. (parseFloat("14,800.00") was returning 14.)
const toNum = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const formatCurrency = (amount) =>
  toNum(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const getStatus = (event) =>
  toNum(event.budget) <= 0 ? 'draft' : String(event.eventstatus || '').toLowerCase()

// Gross = ticket sales. Net = sales minus deductions (event.net holds the deduction).
const getMoney = (event) => {
  const gross = toNum(event.sales)
  const net = gross - toNum(event.net)
  return { gross, net }
}

// Keeps the page clear of the side nav in both expanded and collapsed states.
// Finds the sidebar by position/size, measures its right edge, and pads the page
// by whatever overlap the shell leaves. Re-runs whenever the sidebar resizes.
const useClearSideNav = (ref) => {
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const sideNavRight = () => {
      let right = 0
      document
        .querySelectorAll(
          'aside, nav, [class*="sidebar" i], [class*="sidenav" i], [class*="side-nav" i]',
        )
        .forEach((c) => {
          if (c.contains(el) || el.contains(c)) return
          const cs = getComputedStyle(c)
          if (cs.display === 'none' || cs.visibility === 'hidden') return
          const r = c.getBoundingClientRect()
          if (
            r.left <= 1 &&
            r.width >= 40 &&
            r.width <= 400 &&
            r.height >= window.innerHeight * 0.5
          ) {
            right = Math.max(right, r.right)
          }
        })
      return right
    }

    const update = () => {
      el.style.paddingLeft = ''
      const base = parseFloat(getComputedStyle(el).paddingLeft) || 0
      const overlap = Math.max(0, Math.round(sideNavRight() - el.getBoundingClientRect().left))
      el.style.paddingLeft = overlap ? `${overlap + base}px` : ''
    }

    const ro = new ResizeObserver(update)
    document
      .querySelectorAll(
        'aside, nav, [class*="sidebar" i], [class*="sidenav" i], [class*="side-nav" i]',
      )
      .forEach((c) => ro.observe(c))
    ro.observe(document.body)
    window.addEventListener('resize', update)
    document.addEventListener('transitionend', update)
    update()

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
      document.removeEventListener('transitionend', update)
      el.style.paddingLeft = ''
    }
  }, [ref])
}

const Summary = ({ events }) => {
  const t = useMemo(() => {
    const acc = {
      gross: 0,
      net: 0,
      sold: 0,
      admitted: 0,
      upcoming: 0,
      ongoing: 0,
      past: 0,
      draft: 0,
    }
    events.forEach((e) => {
      const { gross, net } = getMoney(e)
      acc.gross += gross
      acc.net += net
      acc.sold += toNum(e.sold)
      acc.admitted += toNum(e.admissions)
      const s = getStatus(e)
      if (s in acc) acc[s] += 1
    })
    return acc
  }, [events])

  return (
    <section className="ed-summary" aria-label="Summary">
      <div className="ed-card ed-card-money">
        <span className="ed-card-label">Gross</span>
        <strong className="ed-card-value">R{formatCurrency(t.gross)}</strong>
        <span className="ed-card-note">Total ticket sales</span>
      </div>

      <div className="ed-card ed-card-money ed-card-main">
        <span className="ed-card-label">Net</span>
        <strong className="ed-card-value">R{formatCurrency(t.net)}</strong>
        <span className="ed-card-note">Gross minus fees</span>
      </div>

      <div className="ed-card">
        <span className="ed-card-label">Tickets sold</span>
        <strong className="ed-card-value">{t.sold}</strong>
        <span className="ed-card-note">{t.admitted} admitted at the door</span>
      </div>

      <div className="ed-card">
        <span className="ed-card-label">Events</span>
        <strong className="ed-card-value">{events.length}</strong>
        <span className="ed-card-note">
          {t.upcoming} upcoming, {t.ongoing} ongoing, {t.past} past
          {t.draft > 0 ? `, ${t.draft} draft` : ''}
        </span>
      </div>
    </section>
  )
}

const EventRow = ({ event }) => {
  const { gross, net } = getMoney(event)
  const organiser = event.organiser || event.businessname || ''
  const status = getStatus(event)
  const payout = String(event.payoutstatus || '').toLowerCase()
  const running = event.campaignstatus === 'running'

  return (
    <tr>
      <td data-label="Event" className="ed-event-cell">
        <span className="ed-event-name" title={event.eventname}>
          {event.is_sponsorship == 1 && <IoStar className="ed-star" aria-label="Sponsored" />}
          <span className="ed-clip">{event.eventname}</span>
        </span>
        <span className="ed-event-org" title={organiser}>
          {organiser}
        </span>
      </td>
      <td data-label="Date" className="ed-nowrap">
        {event.eventday}
      </td>
      <td data-label="Status">
        <span className={`ed-pill ed-pill-${status}`}>
          {status === 'draft' ? 'Draft' : event.eventstatus}
        </span>
      </td>
      <td data-label="Sold" className="ed-num">
        {toNum(event.sold)}
      </td>
      <td data-label="Admitted" className="ed-num">
        {toNum(event.admissions)}
      </td>
      <td data-label="Gross" className="ed-num ed-amount">
        R{formatCurrency(gross)}
      </td>
      <td data-label="Net" className="ed-num ed-amount">
        R{formatCurrency(net)}
      </td>
      <td data-label="Payout">
        <span className={`ed-pill ed-pill-${payout}`}>{event.payoutstatus}</span>
      </td>
      <td data-label="Visibility">
        <span className={`ed-pill ${running ? 'ed-pill-active' : 'ed-pill-inactive'}`}>
          {running ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td data-label="" className="ed-action-cell">
        <Link
          className="ed-btn ed-btn-quiet"
          to={{
            pathname: './editevent',
            state: {
              is_sponsorship: event.is_sponsorship,
              campaignstatus: event.campaignstatus,
              eventid: event.eventid,
              eventname: event.eventname,
              poster: event.poster,
              eventdate: event.eventday,
              enddate: event.enddate,
              event_date: event.event_day,
              finish_date: event.finish_day,
              theme: event.theme,
              venue: event.venue,
              expectednum: event.expected,
              time: event.time,
              endtime: event.endtime,
              prevpage: 'myevents',
            },
          }}
        >
          View
        </Link>
      </td>
    </tr>
  )
}

const EventsDashboard = () => {
  const [eventList, setEventList] = useState([])
  const [loader, setLoader] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const pageRef = useRef(null)
  useClearSideNav(pageRef)

  useEffect(() => {
    // Totals are now computed from this one list, so the summary always matches the table.
    Axios.get(`${url}?query=EVENT-LIST`, { withCredentials: true })
      .then((response) => {
        setEventList(response.data.status === 'success' ? response.data.events : [])
      })
      .catch((error) => {
        setEventList([])
        console.error('Error fetching dashboard data:', error)
      })
      .finally(() => setLoader(false))
  }, [])

  const totalPages = Math.max(1, Math.ceil(eventList.length / itemsPerPage))
  const currentEvents = eventList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  )
  const accountId = localStorage.getItem('businessid')

  return (
    <div className="events-dashboard ed-page" ref={pageRef}>
      <header className="ed-head">
        <div>
          <h1 className="ed-title">Dashboard</h1>
          <p className="ed-sub">
            {eventList.length} {eventList.length === 1 ? 'event' : 'events'}
          </p>
        </div>

        <div className="ed-head-actions">
          {accountId == 120 && (
            <Link
              className="ed-btn ed-btn-quiet"
              to={{ pathname: 'index-events', state: { prevpage: 'dashboard' } }}
            >
              <IoIosAdd size={20} /> Index events
            </Link>
          )}
          <Link
            className="ed-btn ed-btn-primary"
            to={{ pathname: 'addevent', state: { prevpage: 'dashboard' } }}
          >
            <IoIosAdd size={20} /> Create event
          </Link>
        </div>
      </header>

      <Summary events={eventList} />

      <div className="ed-table-wrap">
        <table className="ed-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Date</th>
              <th>Status</th>
              <th className="ed-num">Sold</th>
              <th className="ed-num">Admitted</th>
              <th className="ed-num">Gross</th>
              <th className="ed-num">Net</th>
              <th>Payout</th>
              <th>Visibility</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loader ? (
              <tr className="ed-state-row">
                <td colSpan={10}>
                  <ClipLoader color="grey" loading size={20} />
                </td>
              </tr>
            ) : currentEvents.length === 0 ? (
              <tr className="ed-state-row">
                <td colSpan={10}>
                  <p>No events yet.</p>
                  <Link
                    className="ed-btn ed-btn-primary"
                    to={{ pathname: 'addevent', state: { prevpage: 'dashboard' } }}
                  >
                    <IoIosAdd size={20} /> Create your first event
                  </Link>
                </td>
              </tr>
            ) : (
              currentEvents.map((event) => <EventRow key={event.eventid} event={event} />)
            )}
          </tbody>
        </table>

        {eventList.length > itemsPerPage && (
          <nav className="ed-pager" aria-label="Pagination">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </div>
  )
}

export default EventsDashboard
